import type { Feature } from './catalog';
import { MARKET_KITS } from './market-kits';
import { DEPARTMENT_KITS, INTERN_KITS } from './departments';

/**
 * Every tool in the CRM, with a stable id. A tool either opens one of the CRM's own pages
 * (page) or its own working space of records (kind decides which fields it shows first).
 */
export type ToolKind = 'records' | 'metrics' | 'ledger' | 'calendar' | 'checklist' | 'library';
export interface Tool { id: string; name: string; note?: string; page?: string; kind: ToolKind; group: string; groupName: string }

export const slug = (s: string) => s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 90);

export function kindOf(name: string): ToolKind {
  const n = name.toLowerCase();
  if (/checklist|wizard|onboarding|offboarding/.test(n)) return 'checklist';
  if (/calendar|schedul|planner|booking|booker|coordinator|organizer|organiser|meeting/.test(n)) return 'calendar';
  if (/ledger|invoice|expense|budget|fee|cost|payroll|refund|commission|rebate|reimburse|billing|p&l|cash|balance|revenue|tax|spend|price|pricing|payout|settlement|reserve|capital|treasury|allocation/.test(n)) return 'ledger';
  if (/dashboard|monitor|gauge|heatmap|chart|analytics|scorecard|kpi|metric|stream|visuali|overview|trend|forecast|feed|report/.test(n)) return 'metrics';
  if (/repository|library|locker|vault|cabinet|archive|directory|registry|room|kit|templates?|editor|builder|factory|workspace|hub|desk/.test(n)) return 'library';
  return 'records';
}

const fromKit = (prefix: string, kits: typeof DEPARTMENT_KITS) => kits.flatMap((d) => d.features.map((f: Feature): Tool => ({
  id: `${prefix}${d.id}.${slug(f.name)}`, name: f.name, note: f.note, page: f.status === 'live' ? f.page : undefined, kind: kindOf(f.name), group: d.id, groupName: d.name,
})));

export const STAFF_TOOLS = fromKit('', DEPARTMENT_KITS);
export const INTERN_TOOLS = fromKit('intern.', INTERN_KITS);

/** Each market's desk in the CRM, from the per-market feature lists. */
export function marketTools(brand: string): Tool[] {
  return (MARKET_KITS[brand] ?? []).flatMap((sec) => sec.features.map((f): Tool => ({
    id: `market.${brand}.${slug(f.name)}`, name: f.name, note: f.note || undefined, page: f.page ?? undefined, kind: kindOf(f.name), group: slug(sec.name).slice(0, 40), groupName: sec.name,
  })));
}

export function findTool(id: string): Tool | undefined {
  if (id.startsWith('market.')) return marketTools(id.split('.')[1]!).find((t) => t.id === id);
  return (id.startsWith('intern.') ? INTERN_TOOLS : STAFF_TOOLS).find((t) => t.id === id);
}

export const KIND_HELP: Record<ToolKind, string> = {
  records: 'Track each item with an owner, status and due date. Discuss it and attach files.',
  metrics: 'Live firm numbers from the CRM, with a log of findings and actions for your team.',
  ledger: 'Record each item with its amount and currency; totals update as you go.',
  calendar: 'Plan each item on a date, assign it and follow it through.',
  checklist: 'Work through each item step by step and tick it off when done.',
  library: 'Keep documents, templates and notes in one shared place, with files attached.',
};
